import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { SchoolId, TeamId } from '@suntex.fim/api-client';
import { isPlayerLoginType } from '@suntex.fim/auth';
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { TeamTopPlayersType } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';

import { TeamContext as ITeamContext, TeamInfo } from './types';

const TeamContext = createContext<ITeamContext | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useTeamContext(): ITeamContext {
  const context = useContext(TeamContext);
  if (context === null) {
    throw new Error('useTeamContext must be used within a TeamProvider');
  }
  return context;
}

/** Returns the TeamContext value, or `null` when no TeamProvider is present. */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalTeamContext(): ITeamContext | null {
  return useContext(TeamContext);
}

export interface TeamProviderProps {
  children: ReactNode;
  teamId?: TeamId;
}

interface TeamProviderCoreProps {
  children: ReactNode;
  teamId: TeamId;
}

const isValidId = (id: number): boolean => id > 0;

function TeamProviderCore({ children, teamId }: TeamProviderCoreProps) {
  const { api } = useFramework();
  const { login } = useAuth(isAuthenticated);
  const queryClient = useQueryClient();
  const isPlayer = isPlayerLoginType(login.loginType);

  const { data: teamData, isLoading: isTeamInfoLoading } = useQuery({
    queryKey: ['team', 'info', teamId],
    queryFn: async () => {
      return await api.get_Team_Team({ teamId });
    },
    enabled: !isPlayer && isValidId(teamId),
  });

  // Players can't call get_Team_Team (403), so fetch team info via player summary instead
  const { data: playerTeamData, isLoading: isPlayerTeamLoading } = useQuery({
    queryKey: ['team', 'playerTeamInfo', login.playerId],
    queryFn: async () => {
      return await api.get_Assessment_PlayerSummary({
        playerId: login.playerId,
      });
    },
    enabled: isPlayer && login.playerId > 0,
  });

  const teamInfo: TeamInfo | null = useMemo(() => {
    if (teamData) {
      return {
        id: teamId,
        schoolId: teamData.info.schoolId,
        name: teamData.info.name,
        className: teamData.info.className,
        grade: teamData.info.grade,
        year: teamData.info.year,
      };
    }
    if (playerTeamData) {
      return {
        id: playerTeamData.teamId,
        schoolId: login.schoolId,
        name: playerTeamData.teamName,
        className: undefined,
        grade: playerTeamData.grade,
        year: playerTeamData.year,
      };
    }
    return null;
  }, [teamData, playerTeamData, teamId, login.schoolId]);

  const schoolId = teamInfo?.schoolId ?? (0 as SchoolId);

  const { data: myTeams = [], isLoading: isMyTeamsLoading } = useQuery({
    queryKey: ['team', 'myTeams', teamId, login.id],
    queryFn: async () => {
      const response = await api.get_Team_Index({
        loginId: login.id,
        teamId,
        type: TeamTopPlayersType.AllTime,
      });
      return response.data[0]?.myTeams ?? [];
    },
    enabled: !isPlayer && isValidId(teamId),
  });

  const { data: schoolInfo, isLoading: isSchoolInfoLoading } = useQuery({
    queryKey: [
      'school',
      'info',
      schoolId,
      login.id,
      login.loginType,
      login.userId,
    ],
    queryFn: async () => {
      return await api.get_School_Index({
        loginType: login.loginType,
        loginId: login.id,
        schoolId,
        userId: login.userId,
      });
    },
    enabled: !isPlayer && isValidId(schoolId),
  });

  const reload = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['team', 'info', teamId] });
    void queryClient.invalidateQueries({
      queryKey: ['team', 'playerTeamInfo', login.playerId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['team', 'myTeams', teamId, login.id],
    });
    void queryClient.invalidateQueries({
      queryKey: ['school', 'info', schoolId],
    });
  }, [queryClient, teamId, login.id, login.playerId, schoolId]);

  const value: ITeamContext = useMemo(
    () => ({
      teamId: isValidId(teamId) ? teamId : null,
      schoolId: isValidId(schoolId) ? schoolId : null,
      teamInfo,
      myTeams,
      schoolInfo: schoolInfo ?? null,
      isLoading:
        isTeamInfoLoading ||
        isPlayerTeamLoading ||
        isMyTeamsLoading ||
        isSchoolInfoLoading,
      reload,
    }),
    [
      teamId,
      schoolId,
      teamInfo,
      myTeams,
      schoolInfo,
      isTeamInfoLoading,
      isPlayerTeamLoading,
      isMyTeamsLoading,
      isSchoolInfoLoading,
      reload,
    ],
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

/**
 * Loads team / school data based on the current login (or prop overrides) and
 * exposes them via `useTeam` and per-data hooks. Each query is skipped when
 * its id isn't valid (`<= 0`). Automatically resets when the resolved id
 * changes, so consumers never see stale data from a previous entity.
 */
export function TeamProvider({
  children,
  teamId: teamIdProp,
}: TeamProviderProps) {
  const { login } = useAuth(isAuthenticated);
  const teamId = teamIdProp ?? login.teamId;

  return (
    <TeamProviderCore key={teamId} teamId={teamId}>
      {children}
    </TeamProviderCore>
  );
}
